// ============================================================
// PINNED: CRAWL TO COVER OR GO PRONE (2026-10-24/25).
//
// The book: a figure pinned "must take cover or go prone immediately" - on the ENEMY's turn. The table puts it
// as a question to the pinned figure's player through the existing asks (the GM answers for an NPC, and may
// answer for anyone): crawl to cover (one crawl move outside his turn, half his Half Move, costing nothing from
// his next turn) or go prone. Whoever answers acts on their own screen.
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from '../roster/types';
import { hasCondition } from '../conditions';

/** The ask's kind on the wire. */
export const PINNED_ASK = 'pinned';

/** What the question carries: who is pinned, by name, and whether he is already down (no "go prone" then). */
export interface PinnedAsk {
  combatantId: string;
  name: string;
  prone: boolean;
}

/** Carries this condition, whatever its rounds or stacks (`condType` reads the code out of `Pn:2`). */
const carries = (c: Combatant | undefined, code: string): boolean => hasCondition(c?.state?.conditions, code);

const isPinned = (c: Combatant | undefined): boolean => carries(c, 'Pn');
const isProne = (c: Combatant | undefined): boolean => carries(c, 'P');

/** A question from the wire is untrusted: read it, or null. */
export function readPinnedAsk(payload: unknown): PinnedAsk | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const p = payload as Record<string, unknown>;
  if (typeof p.combatantId !== 'string' || typeof p.name !== 'string') return null;
  return { combatantId: p.combatantId, name: p.name, prone: p.prone === true };
}

/** The question for this figure. */
export function pinnedAskFor(c: Combatant): PinnedAsk {
  return { combatantId: c.id, name: c.sheet?.name ?? c.id, prone: isProne(c) };
}

/** Everyone pinned now. */
export function pinnedIds(roster: readonly Combatant[]): Set<string> {
  return new Set(roster.filter(isPinned).map((c) => c.id));
}

/** Who became pinned between two reads. */
export function newlyPinned(before: ReadonlySet<string>, now: ReadonlySet<string>): string[] {
  return [...now].filter((id) => !before.has(id));
}

/**
 * The question is over: he shook it off (no longer Pinned), he is gone from the roster, or his own turn began -
 * the book's "immediately" means on the enemy's turn, not his.
 */
export function pinnedAskIsOver(ask: PinnedAsk, roster: readonly Combatant[], actingId: string | null): boolean {
  const c = roster.find((r) => r.id === ask.combatantId);
  return !c || !isPinned(c) || actingId === ask.combatantId;
}

/** How far the crawl goes: half his Half Move, which Pinned has already capped (conditionMovement CRAWLS). */
export function crawlBudget(movement: { half?: number } | null | undefined): number {
  return Math.max(0, (movement?.half ?? 0) / 2);
}
